import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { Service } from '../service/terminal'
import { TRANSITION_DURATION, Z_INDEX } from '../constants'
import { createId } from '../utilities/general'

export type TerminalMode = 'modal' | 'fullscreen' | 'minimized'

export type TerminalSession = {
  id: string
  serviceName: string
  mode: TerminalMode
  openedFromRoute?: boolean
  previousMode?: TerminalMode
  position?: { x: number; y: number }
  savedPosition?: { x: number; y: number }
  size?: { width: number; height: number }
  savedSize?: { width: number; height: number }
  zIndex: number
  isActive: boolean
  lastActive: number
  closing: boolean
}

type OpenSessionInput = {
  serviceName: string
  mode?: TerminalMode
  openedFromRoute?: boolean
}

type TerminalSessionsState = {
  sessions: TerminalSession[]
  getActiveSession: () => TerminalSession | null
  openSession: (input: OpenSessionInput) => string
  setMode: (id: string, mode: TerminalMode) => void
  setPosition: (id: string, position: { x: number; y: number }) => void
  setSize: (id: string, size: { width: number; height: number }) => void
  focusSession: (id: string, mode?: TerminalMode) => void
  requestClose: (id: string) => void
  closeSession: (id: string) => void
  reset: () => void
}

const MAX_SESSIONS = 25

// --- Service registry (non-persisted, module-level) ---

const serviceRegistry = new Map<string, Service>()

export const getService = (id: string): Service | null => serviceRegistry.get(id) ?? null

export const registerService = (id: string, service: Service) => {
  serviceRegistry.set(id, service)
}

export const disposeService = (id: string) => {
  const service = serviceRegistry.get(id)
  if (service) {
    service.dispose()
    serviceRegistry.delete(id)
  }
}

// --- Helpers ---

const calculateModeTransition = (
  currentMode: TerminalMode,
  newMode: TerminalMode,
  previousMode?: TerminalMode
) => {
  const mode = currentMode === 'minimized' && newMode === 'modal' && previousMode ? previousMode : newMode
  const updatedPreviousMode = newMode === 'minimized' && currentMode !== 'minimized' ? currentMode : previousMode
  return { mode, previousMode: updatedPreviousMode }
}

const reassignZIndexes = (sessions: TerminalSession[], focusedId: string): TerminalSession[] => {
  const rankOrder = [...sessions].sort((a, b) => a.zIndex - b.zIndex).map(s => s.id)
  const zMap = new Map<string, number>()
  let next = Z_INDEX.TERMINAL_BASE
  for (const id of rankOrder) {
    zMap.set(id, id === focusedId ? Z_INDEX.TERMINAL_BASE + sessions.length - 1 : next++)
  }
  return sessions.map(s => ({ ...s, zIndex: zMap.get(s.id)! }))
}

const shouldAllowHydratedFullscreen = () => {
  if (typeof window === 'undefined') return false
  return /^\/terminal\/[^/]+\/?$/.test(window.location.pathname)
}

// --- Store ---

const useTerminal = create<TerminalSessionsState>()(
  persist(
    (set, get) => {
      const autoFocus = (excludeId: string) => {
        const next = get()
          .sessions.filter(s => s.id !== excludeId && s.mode !== 'minimized')
          .sort((a, b) => b.lastActive - a.lastActive)[0]
        if (next) get().focusSession(next.id)
      }

      return {
        sessions: [],
        getActiveSession: () => {
          return get().sessions.find(session => session.isActive) || null
        },
        openSession: ({ serviceName, mode = 'modal', openedFromRoute = false }) => {
          const id = createId()
          const now = Date.now()
          let removedId: string | undefined

          set(state => {
            let sessions = state.sessions
            if (sessions.length >= MAX_SESSIONS) {
              const oldest = [...sessions].sort((a, b) => a.lastActive - b.lastActive)[0]
              if (oldest) {
                removedId = oldest.id
                sessions = sessions.filter(s => s.id !== oldest.id)
              }
            }
            return {
              sessions: [
                ...sessions.map(s => ({ ...s, isActive: false })),
                {
                  id,
                  serviceName,
                  mode,
                  openedFromRoute,
                  zIndex: Z_INDEX.TERMINAL_BASE + sessions.length,
                  isActive: true,
                  lastActive: now,
                  closing: false,
                },
              ],
            }
          })

          if (removedId) disposeService(removedId)
          return id
        },
        setMode: (id, mode) => {
          const target = get().sessions.find(s => s.id === id)
          if (!target) return

          set(state => ({
            sessions: state.sessions.map(session => {
              if (session.id !== id) return session
              const { mode: newMode, previousMode } = calculateModeTransition(session.mode, mode, session.previousMode)
              const enteringFullscreen = newMode === 'fullscreen' && session.mode !== 'fullscreen'
              const leavingFullscreen = newMode !== 'fullscreen' && session.mode === 'fullscreen'
              return {
                ...session,
                mode: newMode,
                previousMode,
                lastActive: Date.now(),
                isActive: newMode === 'minimized' ? false : session.isActive,
                savedPosition: enteringFullscreen ? session.position : session.savedPosition,
                savedSize: enteringFullscreen ? session.size : session.savedSize,
                position: leavingFullscreen ? session.savedPosition : session.position,
                size: leavingFullscreen ? session.savedSize : session.size,
              }
            }),
          }))

          if (mode === 'minimized' && target.isActive) autoFocus(id)
        },
        setPosition: (id, position) => {
          set(state => ({
            sessions: state.sessions.map(session => (session.id === id ? { ...session, position } : session)),
          }))
        },
        setSize: (id, size) => {
          set(state => ({
            sessions: state.sessions.map(session => (session.id === id ? { ...session, size } : session)),
          }))
        },
        focusSession: (id, newMode?) => {
          const now = Date.now()
          set(state => {
            const targetSession = state.sessions.find(s => s.id === id)
            if (!targetSession) return state

            const modeTransition = newMode
              ? calculateModeTransition(targetSession.mode, newMode, targetSession.previousMode)
              : { mode: targetSession.mode, previousMode: targetSession.previousMode }

            const enteringFullscreen = modeTransition.mode === 'fullscreen' && targetSession.mode !== 'fullscreen'
            const leavingFullscreen = modeTransition.mode !== 'fullscreen' && targetSession.mode === 'fullscreen'

            const withReassignedZIndexes = reassignZIndexes(state.sessions, id)

            return {
              sessions: withReassignedZIndexes.map(session => ({
                ...session,
                isActive: session.id === id,
                lastActive: session.id === id ? now : session.lastActive,
                mode: session.id === id ? modeTransition.mode : session.mode,
                previousMode: session.id === id ? modeTransition.previousMode : session.previousMode,
                ...(session.id === id && enteringFullscreen
                  ? { savedPosition: session.position, savedSize: session.size }
                  : {}),
                ...(session.id === id && leavingFullscreen
                  ? { position: session.savedPosition, size: session.savedSize }
                  : {}),
              })),
            }
          })
        },
        closeSession: id => {
          const session = get().sessions.find(item => item.id === id)
          const wasActive = session?.isActive
          disposeService(id)
          set(state => ({
            sessions: state.sessions.filter(item => item.id !== id),
          }))
          if (wasActive) autoFocus(id)
        },
        requestClose: id => {
          const session = get().sessions.find(item => item.id === id)
          if (!session || session.closing) return
          set(state => ({
            sessions: state.sessions.map(item => (item.id === id ? { ...item, closing: true } : item)),
          }))
          window.setTimeout(() => get().closeSession(id), TRANSITION_DURATION)
        },
        reset: () => {
          serviceRegistry.forEach(service => service.dispose())
          serviceRegistry.clear()
          set({ sessions: [] })
          if (typeof sessionStorage !== 'undefined') {
            sessionStorage.removeItem('terminalSessions')
          }
        },
      }
    },
    {
      name: 'terminalSessions',
      storage: createJSONStorage(() => sessionStorage),
      partialize: state => ({
        ...state,
        sessions: state.sessions.filter(s => !s.openedFromRoute),
      }),
      merge: (persistedState, currentState) => {
        const data = persistedState as { sessions?: TerminalSession[] }
        const allowHydratedFullscreen = shouldAllowHydratedFullscreen()
        return {
          ...currentState,
          sessions: (data?.sessions || []).map(session => ({
            ...session,
            mode:
              !allowHydratedFullscreen && session.mode === 'fullscreen'
                ? 'modal'
                : session.mode,
            previousMode:
              !allowHydratedFullscreen && session.previousMode === 'fullscreen'
                ? 'modal'
                : session.previousMode,
            closing: false,
          })),
        }
      },
    }
  )
)

export default useTerminal
